import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ChatbotRequest {
  query: string;
  conversationId?: string;
  user?: string;
  portal?: string;
}

export interface A2UIContentBlockDelta {
  type: 'content_block_delta';
  delta: {
    type: 'text_delta' | 'thinking' | 'file';
    text?: string;
    thought?: string;
    url?: string;
    file_type?: string;
  };
  conversation_id?: string;
}

export interface A2UIMessageEnd {
  type: 'message_end';
  metadata: {
    conversation_id?: string;
    message_id?: string;
    usage?: any;
  };
}

export interface A2UIError {
  type: 'error';
  message: string;
  code?: string;
}

export type A2UIEvent = A2UIContentBlockDelta | A2UIMessageEnd | A2UIError;

export interface ChatHistoryItem {
  id: number;
  userId: string;
  conversationId: string;
  title: string;
  category: string | null;
  updateTime: string;
}

export interface ChatMessageVO {
  role: string;
  content: string;
  timestamp: string;
}

export interface ChatMessagesRes {
  historyId: number;
  conversationId: string;
  messages: ChatMessageVO[];
}

export interface ChatHistorySaveReq {
  userId: string;
  conversationId?: string;
  messages: { role: string; content: string; timestamp: string }[];
}

export interface ChatTitleRes {
  historyId: number;
  title: string;
  category: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private baseUrl = environment.dealerPortalUrl;

  constructor(private ngZone: NgZone, private http: HttpClient) {}

  uploadImage(file: File, type: string, query?: string, user?: string): Observable<A2UIEvent> {
    return new Observable<A2UIEvent>(observer => {
      const url = `${this.baseUrl}/frontchat/upload`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      if (query) {
        formData.append('query', query);
      }
      if (user) {
        formData.append('user', user);
      }

      fetch(url, {
        method: 'POST',
        headers: this.buildFetchHeaders(),
        body: formData
      }).then(response => {
        if (!response.ok) {
          response.text().then(body => {
            this.ngZone.run(() => observer.error(new Error(`HTTP ${response.status}: ${body || response.statusText}`)));
          });
          return;
        }
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedData = false;

        const read = (): void => {
          reader.read().then(({ done, value }) => {
            if (done) {
              if (!receivedData) {
                this.ngZone.run(() => observer.error(new Error('服务端未返回任何数据')));
              } else {
                this.ngZone.run(() => observer.complete());
              }
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith('data:')) {
                const jsonStr = trimmed.slice(5).trim();
                if (jsonStr) {
                  try {
                    const event: A2UIEvent = JSON.parse(jsonStr);
                    receivedData = true;
                    this.ngZone.run(() => observer.next(event));
                  } catch (e) {
                    console.warn('[Chatbot] Failed to parse SSE data:', jsonStr, e);
                  }
                }
              }
            }

            read();
          }).catch(err => {
            this.ngZone.run(() => observer.error(err));
          });
        };

        read();
      }).catch(err => {
        this.ngZone.run(() => observer.error(err));
      });
    });
  }

  chat(request: ChatbotRequest): Observable<A2UIEvent> {
    return new Observable<A2UIEvent>(observer => {
      const url = `${this.baseUrl}/frontchat/chat`;
      console.log('[Chatbot] Connecting to:', url);

      // AbortController: 150s overall timeout to prevent indefinite hang
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('[Chatbot] Request timed out after 150s');
        controller.abort();
      }, 150_000);

      fetch(url, {
        method: 'POST',
        headers: this.buildFetchHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(request),
        signal: controller.signal
      }).then(response => {
        console.log('[Chatbot] Response status:', response.status, response.headers.get('content-type'));
        if (!response.ok) {
          clearTimeout(timeoutId);
          response.text().then(body => {
            this.ngZone.run(() => observer.error(new Error(`HTTP ${response.status}: ${body || response.statusText}`)));
          });
          return;
        }
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedData = false;

        const read = (): void => {
          reader.read().then(({ done, value }) => {
            if (done) {
              clearTimeout(timeoutId);
              console.log('[Chatbot] Stream ended. Received data:', receivedData);
              if (!receivedData) {
                this.ngZone.run(() => observer.error(new Error('服务端未返回任何数据，请检查后端Dify配置')));
              } else {
                this.ngZone.run(() => observer.complete());
              }
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            console.log('[Chatbot] Chunk received:', chunk);
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith('data:')) {
                const jsonStr = trimmed.slice(5).trim();
                if (jsonStr) {
                  try {
                    const event: A2UIEvent = JSON.parse(jsonStr);
                    // Surface backend error events to the user immediately
                    if (event.type === 'error') {
                      clearTimeout(timeoutId);
                      receivedData = true;
                      this.ngZone.run(() => {
                        observer.next(event);
                        observer.complete();
                      });
                      reader.cancel();
                      return;
                    }
                    receivedData = true;
                    this.ngZone.run(() => observer.next(event));
                  } catch (e) {
                    console.warn('[Chatbot] Failed to parse SSE data:', jsonStr, e);
                  }
                }
              }
              // SSE comment lines (e.g. ': keepalive') are silently ignored,
              // but their arrival keeps the TCP connection alive in the browser.
            }

            read();
          }).catch(err => {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') {
              console.warn('[Chatbot] Request aborted (timeout)');
              this.ngZone.run(() => observer.error(new Error('请求超时，请重新发送消息')));
            } else {
              console.error('[Chatbot] Stream read error:', err);
              this.ngZone.run(() => observer.error(err));
            }
          });
        };

        read();
      }).catch(err => {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.warn('[Chatbot] Request aborted (timeout)');
          this.ngZone.run(() => observer.error(new Error('请求超时，请重新发送消息')));
        } else {
          console.error('[Chatbot] Fetch error:', err);
          this.ngZone.run(() => observer.error(err));
        }
      });

      // Cleanup: abort the request when the subscriber unsubscribes
      return () => {
        clearTimeout(timeoutId);
        controller.abort();
      };
    });
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token') || '';
    const config: any = localStorage.getItem('template_config') || '{}';
    const { language = 'zh' } = JSON.parse(config);
    return {
      'Content-Type': 'application/json',
      'token': token,
      'Accept-Language': language === 'en' ? 'en-US' : 'zh-CN',
      'X-APP-SYSTEM': '6',
      'isDealerPortal': 'true'
    };
  }

  private getOrgCode(router: string): string {
    const orgMapping = JSON.parse(localStorage.getItem('allOrgRoutes') || '{}');
    const userOrg = JSON.parse(localStorage.getItem('sales_org') || '[]');
    const orgList = orgMapping[router];
    if (window.location.href.includes('Salesportal/iam/cart/gettoken')) {
      return 'CN20-G1-40';
    }
    if (!orgList || orgList.length === 0) {
      return '';
    }
    if (orgList.includes('CN20-G1-40') && userOrg.includes('CN20-G1-40')) {
      return 'CN20-G1-40';
    }
    const firstMatchingOrg = orgList.find((org: any) => userOrg.includes(org));
    return firstMatchingOrg ? firstMatchingOrg : orgList[0];
  }

  private buildFetchHeaders(extra?: Record<string, string>): Record<string, string> {
    const token = localStorage.getItem('token') || '';
    const config: any = localStorage.getItem('template_config') || '{}';
    const { language = 'zh' } = JSON.parse(config);
    const headers: Record<string, string> = {
      'token': token,
      'Accept-Language': language === 'en' ? 'en-US' : 'zh-CN',
      'X-APP-SYSTEM': '6'
    };

    const customer = localStorage.getItem('customer');
    if (customer) {
      try {
        const cc = JSON.parse(customer).customerCode;
        if (cc) headers['CUSTOMER-CODE'] = cc;
      } catch {}
    }

    const orgPaths = ['/iam', '/oes'];
    const matchedOrg = orgPaths.find(p => window.location.pathname.includes(p));
    if (matchedOrg && token) {
      headers['ORG'] = this.getOrgCode(matchedOrg);
    } else {
      headers['ORG'] = 'CN20-G1-40';
    }

    return extra ? { ...headers, ...extra } : headers;
  }

  getChatHistory(): Observable<{ rtnCode: number; rtnData: ChatHistoryItem[] }> {
    return this.http.get<{ rtnCode: number; rtnData: ChatHistoryItem[] }>(
      `/frontchat/history`,
      { headers: this.getAuthHeaders() as any }
    );
  }

  saveChatHistory(req: ChatHistorySaveReq): Observable<{ rtnCode: number; rtnData: number }> {
    return this.http.post<{ rtnCode: number; rtnData: number }>(
      `/frontchat/history`,
      req,
      { headers: this.getAuthHeaders() as any }
    );
  }

  generateTitle(historyId: number, firstMessage: string): Observable<{ rtnCode: number; rtnData: ChatTitleRes }> {
    return this.http.post<{ rtnCode: number; rtnData: ChatTitleRes }>(
      `/frontchat/history/${historyId}/title`,
      { firstMessage },
      { headers: this.getAuthHeaders() as any }
    );
  }

  getChatMessages(historyId: number): Observable<{ rtnCode: number; rtnData: ChatMessagesRes }> {
    return this.http.get<{ rtnCode: number; rtnData: ChatMessagesRes }>(
      `/frontchat/history/${historyId}/messages`,
      { headers: this.getAuthHeaders() as any }
    );
  }

  deleteHistory(historyId: number): Observable<{ rtnCode: number; rtnData: null }> {
    return this.http.delete<{ rtnCode: number; rtnData: null }>(
      `/frontchat/history/${historyId}`,
      { headers: this.getAuthHeaders() as any }
    );
  }
}
